import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import Table from "../../../../applicationUi/components/Table";
import { format } from "date-fns";
import { fetchClinicPerformanceReport } from "../../../../store/thunks/reportsThunk";
import * as XLSX from "xlsx";
import { toast } from 'react-toastify';

// Currency formatting utility
const formatCurrency = (amount) => {
  if (amount == null) return "$0.00";
  return `$${Number(amount).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;
};

// Percentage formatting utility
const formatPercentage = (value) => {
  if (value == null) return "0.0%";
  return `${Number(value).toFixed(1)}%`;
};

const ClinicPerformanceReport = () => {
  const dispatch = useDispatch();
  const [startDate, setStartDate] = useState(format(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), "yyyy-MM-dd"));
  const [endDate, setEndDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [selectedClinic, setSelectedClinic] = useState("all");
  const [activeView, setActiveView] = useState("summary");

  const {
    data: performanceData,
    loading,
    error,
  } = useSelector((state) => state.reports.clinicPerformance || { data: null, loading: false, error: null });

  const { data: clinics = [] } = useSelector((state) => state.clinic || { data: [] });

  const data = performanceData?.data || {};
  const summary = data.summary || {};
  const byClinic = data.byClinic || [];
  const byClinician = data.byClinician || [];
  const byService = data.byService || [];
  const dailyTrends = data.dailyTrends || [];

  // Load initial data on component mount
  useEffect(() => {
    const initialStartDate = format(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), "yyyy-MM-dd");
    const initialEndDate = format(new Date(), "yyyy-MM-dd");
    dispatch(fetchClinicPerformanceReport({ startDate: initialStartDate, endDate: initialEndDate, clinicId: "all" }));
  }, [dispatch]);

  const handleSubmit = (e) => {
    e.preventDefault();
    dispatch(fetchClinicPerformanceReport({ startDate, endDate, clinicId: selectedClinic }));
  };

  const handleDownload = (viewType) => {
    let data = [];
    let fileName = "";
    let sheetName = "";

    switch (viewType) {
      case "clinic":
        data = byClinic.map((item) => ({
          "Clinic": item.clinicName || "Unknown",
          "Total Appointments": item.totalAppointments || 0,
          "Treated": item.treatedAppointments || 0,
          "Cancelled": item.cancelledAppointments || 0,
          "No Shows": item.noShowAppointments || 0,
          "Treatment Rate": formatPercentage(item.treatmentRate),
          "Cancellation Rate": formatPercentage(item.cancellationRate),
          "No Show Rate": formatPercentage(item.noShowRate),
          "Total Hours": Number(item.totalHours || 0).toFixed(2),
        }));
        fileName = `Performance_by_Clinic_${startDate}_to_${endDate}.xlsx`;
        sheetName = "By Clinic";
        break;
      case "clinician":
        data = byClinician.map((item) => ({
          "Clinician": item.clinicianName || "Unknown",
          "Total Appointments": item.totalAppointments || 0,
          "Treated": item.treatedAppointments || 0,
          "Cancelled": item.cancelledAppointments || 0,
          "No Shows": item.noShowAppointments || 0,
          "Treatment Rate": formatPercentage(item.treatmentRate),
          "Cancellation Rate": formatPercentage(item.cancellationRate),
          "No Show Rate": formatPercentage(item.noShowRate),
          "Total Hours": Number(item.totalHours || 0).toFixed(2),
        }));
        fileName = `Performance_by_Clinician_${startDate}_to_${endDate}.xlsx`;
        sheetName = "By Clinician";
        break;
      case "service":
        data = byService.map((item) => ({
          "Service Type": item.serviceType || "Unknown",
          "Total Appointments": item.totalAppointments || 0,
          "Treated": item.treatedAppointments || 0,
          "Cancelled": item.cancelledAppointments || 0,
          "No Shows": item.noShowAppointments || 0,
          "Treatment Rate": formatPercentage(item.treatmentRate),
          "Cancellation Rate": formatPercentage(item.cancellationRate),
          "No Show Rate": formatPercentage(item.noShowRate),
          "Total Hours": Number(item.totalHours || 0).toFixed(2),
        }));
        fileName = `Performance_by_Service_${startDate}_to_${endDate}.xlsx`;
        sheetName = "By Service";
        break;
      case "daily":
        data = dailyTrends.map((item) => ({
          "Date": item.date,
          "Total Appointments": item.totalAppointments || 0,
          "Treated": item.treatedAppointments || 0,
          "Cancelled": item.cancelledAppointments || 0,
          "No Shows": item.noShowAppointments || 0,
          "Treatment Rate": formatPercentage(item.treatmentRate),
        }));
        fileName = `Daily_Performance_${startDate}_to_${endDate}.xlsx`;
        sheetName = "Daily Trends";
        break;
      default:
        return;
    }

    if (data.length === 0) {
      toast.warning("No data available to download.");
      return;
    }

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    XLSX.writeFile(workbook, fileName);
  };

  const clinicColumns = [
    { header: "Clinic", accessor: "clinicName" },
    { header: "Total Appointments", accessor: "totalAppointments" },
    { header: "Treated", accessor: "treatedAppointments" },
    { header: "Cancelled", accessor: "cancelledAppointments" },
    { header: "No Shows", accessor: "noShowAppointments" },
    { header: "Treatment Rate", accessor: (row) => formatPercentage(row.treatmentRate) },
    { header: "Cancellation Rate", accessor: (row) => formatPercentage(row.cancellationRate) },
    { header: "No Show Rate", accessor: (row) => formatPercentage(row.noShowRate) },
    { header: "Total Hours", accessor: (row) => Number(row.totalHours || 0).toFixed(2) },
  ];

  const clinicianColumns = [
    { header: "Clinician", accessor: "clinicianName" },
    { header: "Total Appointments", accessor: "totalAppointments" },
    { header: "Treated", accessor: "treatedAppointments" },
    { header: "Cancelled", accessor: "cancelledAppointments" },
    { header: "No Shows", accessor: "noShowAppointments" },
    { header: "Treatment Rate", accessor: (row) => formatPercentage(row.treatmentRate) },
    { header: "Cancellation Rate", accessor: (row) => formatPercentage(row.cancellationRate) },
    { header: "No Show Rate", accessor: (row) => formatPercentage(row.noShowRate) },
    { header: "Total Hours", accessor: (row) => Number(row.totalHours || 0).toFixed(2) },
  ];

  const serviceColumns = [
    { header: "Service Type", accessor: "serviceType" },
    { header: "Total Appointments", accessor: "totalAppointments" },
    { header: "Treated", accessor: "treatedAppointments" },
    { header: "Cancelled", accessor: "cancelledAppointments" },
    { header: "No Shows", accessor: "noShowAppointments" },
    { header: "Treatment Rate", accessor: (row) => formatPercentage(row.treatmentRate) },
    { header: "Cancellation Rate", accessor: (row) => formatPercentage(row.cancellationRate) },
    { header: "No Show Rate", accessor: (row) => formatPercentage(row.noShowRate) },
    { header: "Total Hours", accessor: (row) => Number(row.totalHours || 0).toFixed(2) },
  ];

  const dailyColumns = [
    { header: "Date", accessor: "date" },
    { header: "Total Appointments", accessor: "totalAppointments" },
    { header: "Treated", accessor: "treatedAppointments" },
    { header: "Cancelled", accessor: "cancelledAppointments" },
    { header: "No Shows", accessor: "noShowAppointments" },
    { header: "Treatment Rate", accessor: (row) => formatPercentage(row.treatmentRate) },
  ];

  if (error) return <div className="text-red-500 p-4">Error: {error}</div>;

  return (
    <div className="space-y-6">
      {/* Date Range and Clinic Filter Form */}
      <form onSubmit={handleSubmit} className="bg-white p-6 rounded-sm shadow-sm">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Generate Clinic Performance Report</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
          <div>
            <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-1">
              Start Date
            </label>
            <input
              type="date"
              id="startDate"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="block w-full rounded-sm border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              required
            />
          </div>
          <div>
            <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 mb-1">
              End Date
            </label>
            <input
              type="date"
              id="endDate"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="block w-full rounded-sm border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              required
            />
          </div>
          <div>
            <label htmlFor="clinic" className="block text-sm font-medium text-gray-700 mb-1">
              Clinic
            </label>
            <select
              id="clinic"
              value={selectedClinic}
              onChange={(e) => setSelectedClinic(e.target.value)}
              className="block w-full rounded-sm border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            >
              <option value="all">All Clinics</option>
              {clinics && Array.isArray(clinics) ? clinics.map((clinic) => (
                <option key={clinic._id} value={clinic._id}>
                  {clinic.name}
                </option>
              )) : null}
            </select>
          </div>
          <div>
            <button
              type="submit"
              disabled={loading}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? "Generating..." : "Generate Report"}
            </button>
          </div>
        </div>
      </form>

      {/* Show loading state */}
      {loading && (
        <div className="flex justify-center items-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Loading clinic performance data...</p>
          </div>
        </div>
      )}

      {/* Show data only if we have it and not loading */}
      {!loading && summary && Object.keys(summary).length > 0 && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white p-6 rounded-sm shadow-sm">
              <h4 className="text-sm font-medium text-gray-500">Total Appointments</h4>
              <p className="text-2xl font-bold text-gray-900">{summary.totalAppointments || 0}</p>
            </div>
            <div className="bg-white p-6 rounded-sm shadow-sm">
              <h4 className="text-sm font-medium text-gray-500">Treatment Rate</h4>
              <p className="text-2xl font-bold text-green-600">{formatPercentage(summary.treatmentRate)}</p>
            </div>
            <div className="bg-white p-6 rounded-sm shadow-sm">
              <h4 className="text-sm font-medium text-gray-500">Cancellation Rate</h4>
              <p className="text-2xl font-bold text-yellow-600">{formatPercentage(summary.cancellationRate)}</p>
            </div>
            <div className="bg-white p-6 rounded-sm shadow-sm">
              <h4 className="text-sm font-medium text-gray-500">No Show Rate</h4>
              <p className="text-2xl font-bold text-red-600">{formatPercentage(summary.noShowRate)}</p>
            </div>
          </div>

          {/* Additional Performance Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white p-6 rounded-sm shadow-sm">
              <h4 className="text-sm font-medium text-gray-500">Total Revenue</h4>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(summary.totalRevenue)}</p>
            </div>
            <div className="bg-white p-6 rounded-sm shadow-sm">
              <h4 className="text-sm font-medium text-gray-500">Revenue per Appointment</h4>
              <p className="text-2xl font-bold text-green-600">{formatCurrency(summary.efficiency?.revenuePerAppointment)}</p>
            </div>
            <div className="bg-white p-6 rounded-sm shadow-sm">
              <h4 className="text-sm font-medium text-gray-500">Revenue per Hour</h4>
              <p className="text-2xl font-bold text-blue-600">{formatCurrency(summary.efficiency?.revenuePerHour)}</p>
            </div>
            <div className="bg-white p-6 rounded-sm shadow-sm">
              <h4 className="text-sm font-medium text-gray-500">Active Clients</h4>
              <p className="text-2xl font-bold text-purple-600">{summary.activeClients || 0}</p>
            </div>
          </div>

          {/* View Tabs */}
          <div className="bg-white shadow-sm rounded-sm">
            <div className="border-b border-gray-200">
              <nav className="-mb-px flex space-x-8" aria-label="Tabs">
                {[
                  { key: "summary", label: "Summary" },
                  { key: "clinic", label: "By Clinic" },
                  { key: "clinician", label: "By Clinician" },
                  { key: "service", label: "By Service" },
                  { key: "daily", label: "Daily Trends" },
                ].map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => setActiveView(tab.key)}
                    className={`${
                      activeView === tab.key
                        ? "border-indigo-500 text-indigo-600"
                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm`}
                  >
                    {tab.label}
                  </button>
                ))}
              </nav>
            </div>

            <div className="p-6">
              {activeView === "summary" && (
                <div className="space-y-6">
                  <h3 className="text-lg font-medium text-gray-900">Performance Summary</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="bg-gray-50 p-4 rounded-sm">
                      <h5 className="text-sm font-medium text-gray-600">Operational Efficiency</h5>
                      <p className="text-lg font-semibold text-gray-900">
                        {Number(summary.efficiency?.appointmentsPerDay || 0).toFixed(1)} appointments/day
                      </p>
                      <p className="text-sm text-gray-500">
                        {Number(summary.efficiency?.hoursPerAppointment || 0).toFixed(1)} hours/appointment
                      </p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-sm">
                      <h5 className="text-sm font-medium text-gray-600">Total Scheduled Hours</h5>
                      <p className="text-lg font-semibold text-gray-900">
                        {Number(summary.totalScheduledHours || 0).toFixed(1)} hours
                      </p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-sm">
                      <h5 className="text-sm font-medium text-gray-600">Total Invoices</h5>
                      <p className="text-lg font-semibold text-gray-900">
                        {summary.totalInvoices || 0}
                      </p>
                      <p className="text-sm text-gray-500">
                        Avg: {formatCurrency(summary.avgInvoiceAmount)}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {activeView === "clinic" && (
                <Table
                  columns={clinicColumns}
                  data={byClinic}
                  title="Performance by Clinic"
                  description="Performance metrics breakdown by clinic location"
                  actions={[
                    {
                      label: "Download Excel",
                      onClick: () => handleDownload("clinic"),
                    },
                  ]}
                />
              )}

              {activeView === "clinician" && (
                <Table
                  columns={clinicianColumns}
                  data={byClinician}
                  title="Performance by Clinician"
                  description="Performance metrics breakdown by individual clinicians"
                  actions={[
                    {
                      label: "Download Excel",
                      onClick: () => handleDownload("clinician"),
                    },
                  ]}
                />
              )}

              {activeView === "service" && (
                <Table
                  columns={serviceColumns}
                  data={byService}
                  title="Performance by Service Type"
                  description="Performance metrics breakdown by appointment/service type"
                  actions={[
                    {
                      label: "Download Excel",
                      onClick: () => handleDownload("service"),
                    },
                  ]}
                />
              )}

              {activeView === "daily" && (
                <Table
                  columns={dailyColumns}
                  data={dailyTrends}
                  title="Daily Performance Trends"
                  description="Day-by-day performance analysis"
                  actions={[
                    {
                      label: "Download Excel",
                      onClick: () => handleDownload("daily"),
                    },
                  ]}
                />
              )}
            </div>
          </div>
        </>
      )}

      {/* Show message if no data and not loading */}
      {!loading && (!summary || Object.keys(summary).length === 0) && (
        <div className="bg-white p-6 rounded-sm shadow-sm text-center">
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Data Available</h3>
          <p className="text-gray-600">
            No performance data found for the selected date range and clinic. Try adjusting your filters or check if there are any appointments in the system for this period.
          </p>
        </div>
      )}
    </div>
  );
};

export default ClinicPerformanceReport;